import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Sparkles, Banknote, FileText, Clock4, TrendingUp, Lock, Briefcase, Flame, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Logo, ShieldMark } from "@/components/Logo";
import { SiteHeader } from "@/components/SiteHeader";
import { Seo, SITE_URL } from "@/components/Seo";
import { getConfig, analyzeIdea } from "@/lib/api";
import { IdeaEntry } from "@/components/IdeaEntry";
import { AudienceToggle } from "@/components/AudienceToggle";
import { PreviewModal } from "@/components/PreviewModal";

const features = [
  {
    icon: Banknote,
    title: "Förder-Orientierung",
    desc: "Orientierung zu Förderprogrammen, die für dein Vorhaben und deinen Standort in Frage kommen können.",
    color: "#10C98D",
  },
  {
    icon: Clock4,
    title: "30-Tage-Plan",
    desc: "Ein konkreter Fahrplan mit den nächsten sinnvollen Schritten für die ersten 30 Tage.",
    color: "#3DEEA6",
  },
  {
    icon: Sparkles,
    title: "Sparring-Fragen",
    desc: "Die typischen Fragen, auf die du dich für Gespräche mit Bank oder Behörden vorbereiten solltest.",
    color: "#F59E0B",
  },
];

export default function Landing() {
  const nav = useNavigate();
  const [founders, setFounders] = useState(null);
  useEffect(() => {
    getConfig().then((c) => setFounders({ remaining: c.founders_remaining, limit: c.founder_limit })).catch(() => {});
  }, []);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Gründio",
      url: SITE_URL,
      logo: `${SITE_URL}/gruendio-logo.png`,
      areaServed: ["DE"],
      description: "KI-gestützte Orientierungshilfe und Erstbewertung für Gründer in Deutschland. Keine Rechts- oder Steuerberatung.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Gründio",
      url: SITE_URL,
      inLanguage: "de-DE",
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "KI-Erstbewertung für Gründer (PDF)",
      image: `${SITE_URL}/gruendio-logo.png`,
      brand: { "@type": "Brand", name: "Gründio" },
      description: "Strukturierte KI-Erstbewertung als Vorbereitungs-Grundlage – mit Förder-Orientierung, Konto-Empfehlungen und Sparring-Fragen.",
      offers: { "@type": "Offer", url: SITE_URL, priceCurrency: "EUR", price: "19.00", availability: "https://schema.org/InStock" },
    },
  ];
  return (
    <div className="min-h-screen">
      <Seo
        title="Gründio – KI-Sparringspartner für Gründer in Deutschland | Erstbewertung in 2 Minuten"
        description="Lass deine Geschäftsidee in 2 Minuten von der KI strukturiert bewerten – mit Förder-Orientierung, Bürokratie-Hinweisen und Konto-Empfehlungen für den deutschen Markt. Keine Rechts- oder Steuerberatung."
        path="/gruender"
        keywords="KI-Erstbewertung Geschäftsidee, Geschäftsidee validieren, Gründung Deutschland, Gründungszuschuss, Förder-Orientierung, Businessplan Vorbereitung"
        jsonLd={jsonLd}
      />
      {/* Top bar */}
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots pointer-events-none opacity-60" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="mb-6"><AudienceToggle /></div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-6 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
              Für Gründer in Deutschland
            </div>

            {founders && founders.remaining > 0 && (
              <div
                data-testid="landing-founder-offer"
                className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800"
              >
                <Flame className="h-3.5 w-3.5 text-amber-500" />
                Einführungspreis 29 € statt 39 € · Limitiert auf die ersten {founders.limit} Gründer · noch {founders.remaining} frei
              </div>
            )}

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] hyphens-none"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-slate-900">Hol dir den </span>
              <span className="brand-gradient-text whitespace-nowrap">Gründio Report</span>
              <span className="text-slate-900"> zu deiner Geschäftsidee.</span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Den <b className="text-slate-900">vollständigen Gründio Report</b> – mit deinem Score, Förder-Orientierung,
              Bürokratie-Hinweisen und 30-Tage-Plan – erhältst du für 29 €.
            </p>

            <div className="mt-8">
              <IdeaEntry compact />
            </div>

            <p data-testid="hero-trust-line" className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-600">
              <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: "#10C98D" }} />
              Ersteinschätzung gratis · Keine Bezahldaten · Kein Login
            </p>

            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed">
              Automatisierte Orientierungshilfe & KI-gestützte Ersteinschätzung.
              Keine Rechts- oder Steuerberatung.
            </p>
          </div>

          {/* RIGHT preview card */}
          <div className="lg:col-span-5 animate-fade-in-up">
            <div className="relative">
              <div className="absolute -inset-2 brand-gradient-bg opacity-30 blur-2xl rounded-3xl" />
              <div className="relative brand-gradient-border rounded-3xl p-7 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs uppercase tracking-widest text-slate-500">Beispiel · Voll-Report</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">Exakter Score</span>
                </div>

                <div className="flex items-end gap-3">
                  <div
                    className="text-7xl font-extrabold tracking-tight brand-gradient-text"
                    style={{ fontFamily: "Outfit" }}
                  >
                    72
                  </div>
                  <div className="pb-2 text-slate-500 text-sm">/ 100 · Starkes Konzept</div>
                </div>
                <div className="mt-1 mb-6 text-xs text-slate-400 uppercase tracking-widest">
                  Realistische Markt-Bewertung
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-green-50/70 border border-green-100">
                    <span className="mt-1 h-2 w-2 rounded-full bg-green-500 shrink-0" />
                    <span><b className="text-slate-900">Marktchance:</b> Klare Nische im B2B-Handwerk.</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/70 border border-amber-100">
                    <span className="mt-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                    <span><b className="text-slate-900">Risiko:</b> Vertriebszugang zu KMUs ist schwer.</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-rose-50/70 border border-rose-100">
                    <span className="mt-1 h-2 w-2 rounded-full bg-rose-500 shrink-0" />
                    <span><b className="text-slate-900">To-do:</b> Vertriebskanäle früh testen.</span>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-slate-200 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Einführungspreis</div>
                    <div className="flex items-baseline gap-2">
                      <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>29,00 €</div>
                      <div className="text-sm text-slate-400 line-through">39 €</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 text-right">PDF über 10 Seiten<br/>Sofort-Download</div>
                </div>
                <div className="mt-4 flex justify-center">
                  <PreviewModal
                    variant="gruender"
                    triggerLabel="Vorschau ansehen"
                    ctaLabel="Jetzt Check starten"
                    onCta={() => nav("/form")}
                    order={[1, 2, 20, 21, 19, 12, 13, 3, 4, 5, 6, 7, 8, 9, 10, 11, 14, 15, 16, 17, 18]}
                    labels={{
                      1: "Score",
                      2: "Score & Inhalt",
                      20: "30-Tage-Plan",
                      21: "30-Tage-Plan",
                      19: "Sparring-Fragen",
                      12: "Förder-Orientierung",
                      13: "Förder-Orientierung",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-2xl p-6 border border-slate-200/70 hover:border-emerald-300 hover:shadow-lg transition-all"
            >
              <div
                className="h-11 w-11 rounded-xl flex items-center justify-center mb-4"
                style={{ background: `${f.color}18`, color: f.color }}
              >
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
                {f.title}
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mt-20 grid md:grid-cols-3 gap-5">
          {[
            { n: "1", title: "Idee kurz beschreiben", desc: "Standort, Status, Idee, Kapital – in ca. 60 Sek. ausgefüllt." },
            { n: "2", title: "Strukturiertes Feedback", desc: "Unser KI-Report liefert dir sofort eine ehrliche, strukturierte Ersteinschätzung zu deiner Idee." },
            {
              n: "3",
              title: "Premium-Erstbewertung",
              desc:
                "PDF-Report (über 10 Seiten) für 29 €: mit Score, Förder-Orientierung, Bürokratie-Hinweisen und 30-Tage-Plan.",
            },
          ].map((s) => (
            <div key={s.n} className="relative pl-14">
              <div
                className="absolute left-0 top-0 h-10 w-10 rounded-xl text-white font-bold flex items-center justify-center brand-gradient-bg shadow-md"
                style={{ fontFamily: "Outfit" }}
              >
                {s.n}
              </div>
              <h4 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
                {s.title}
              </h4>
              <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA dark plate (only place we use dark – frames the logo) */}
        <div className="mt-20 relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full opacity-20 bg-emerald-400 blur-3xl" />
          <div className="relative p-10 text-white grid md:grid-cols-[auto,1fr,auto] gap-6 items-center">
            <div className="hidden md:block">
              <ShieldMark size={64} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> Premium-Erstbewertung
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
                Die strukturierte Grundlage für dein nächstes Gespräch.
              </h2>
              <p className="text-white/70 mt-3 max-w-2xl">
                Über 10 Seiten Feedback mit Score, Förder-Orientierung und 30-Tage-Plan – zum Download.
              </p>
            </div>
            <Button
              data-testid="cta-bottom-btn"
              onClick={() => nav("/form")}
              className="h-14 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
            >
              <FileText className="h-5 w-5 mr-2" /> Check starten
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 grid sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Logo size="sm" />
            <p className="max-w-md leading-relaxed">
              Gründio bietet eine automatisierte Orientierungshilfe und KI-gestützte
              Ersteinschätzung. <b>Keine Rechts- oder Steuerberatung</b> und keine Erfolgsgarantie.
            </p>
          </div>
          <div className="sm:text-right space-y-2">
            <nav className="flex sm:justify-end items-center gap-4 flex-wrap">
              <Link to="/impressum" className="hover:text-slate-900" data-testid="landing-impressum-link">Impressum</Link>
              <Link to="/datenschutz" className="hover:text-slate-900" data-testid="landing-datenschutz-link">Datenschutz</Link>
              <Link to="/agb" className="hover:text-slate-900" data-testid="landing-agb-link">AGB</Link>
              <Link to="/widerruf" className="hover:text-slate-900" data-testid="landing-widerruf-link">Widerruf</Link>
            </nav>
            <div>© {new Date().getFullYear()} Gründio · Stand 2026</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
